import snakeCase from 'lodash/snakeCase'

import { instance } from '@/api/instances'

import { mapServerResToTableData } from './utils'

const fetchClinicalProfileByPersonId = async (store, { personId }) => {
  store.commit('setClinicalProfile', {})

  try {
    if (store.rootState.pillars.personId === personId) {
      const clinicalProfile = await instance.get(
        `persons/${personId}/clinical_profile`
      )

      store.commit('setClinicalProfile', { ...clinicalProfile, personId })
    } else {
      store.commit(
        'errors/setCriticalError',
        {
          type: 'mixed_data',
          context: 'person-clinical-profile'
        },
        { root: true }
      )
    }
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/clinicalProfile/fetchClinicalProfileByPersonId'
      },
      { root: true }
    )
  }
}

const updateHealthMeasurement = async (store, healthMeasurement) => {
  try {
    const normalizedResponse = await instance.put(
      `persons/${healthMeasurement.personId}/clinical_profile/health_measurement`,
      {
        json: healthMeasurement
      }
    )

    store.commit('setHealthMeasurementInfo', normalizedResponse)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/clinicalProfile/updateHealthMeasurement'
      },
      { root: true }
    )
  }
}

const getHealthMeasurementTypes = async store => {
  try {
    const types = await instance.get('health_measurement_types')

    store.commit(
      'setHealthMeasurementTypes',
      types
        .filter(t => !!t.active)
        .map(t => ({ ...t, key: snakeCase(t.key).toLocaleUpperCase() }))
    )
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/clinicalProfile/getHealthMeasurementTypes'
      },
      { root: true }
    )
  }
}

const fetchHealthMeasurements = async (store, personId) => {
  try {
    const fromServer = await instance.get(
      `persons/${personId}/health_measurements`
    )

    store.commit(
      'setHealthMeasurementsTableData',
      mapServerResToTableData(fromServer)
    )
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/clinicalProfile/fetchHealthMeasurements'
      },
      { root: true }
    )
  }
}

const addClinicalBackground = async (store, clinicalBackground) => {
  try {
    const { propertyName, ...payload } = clinicalBackground
    const normalizedResponse = await instance.post(
      `persons/${clinicalBackground.personId}/clinical_profile/clinical_background`,
      { json: payload }
    )

    store.commit('addClinicalBackgroundInfo', {
      clinicalBackground: normalizedResponse,
      propertyName
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/clinicalProfile/addClinicalBackground' },
      { root: true }
    )
  }
}

const updateClinicalBackground = async (store, clinicalBackground) => {
  try {
    const { propertyName, ...payload } = clinicalBackground
    const normalizedResponse = await instance.put(
      `persons/${payload.personId}/clinical_profile/clinical_background/${payload.id}`,
      {
        json: payload
      }
    )

    store.commit('updateClinicalBackgroundInfo', {
      clinicalBackground: normalizedResponse,
      propertyName
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/clinicalProfile/updateClinicalBackground'
      },
      { root: true }
    )
  }
}

const removeClinicalBackground = async (store, clinicalBackground) => {
  try {
    const { id, personId, propertyName } = clinicalBackground
    const normalizedResponse = await instance.delete(
      `persons/${personId}/clinical_profile/clinical_background/${id}`
    )

    store.commit('removeClinicalBackgroundInfo', {
      clinicalBackground: normalizedResponse,
      propertyName
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/clinicalProfile/removeClinicalBackground'
      },
      { root: true }
    )
  }
}

const addCpt = async (store, clinicalBackground) => {
  try {
    const { personId, propertyName } = clinicalBackground
    const normalizedResponse = await instance.post(
      `persons/${personId}/clinical_profile/cpts`,
      {
        json: {
          ...clinicalBackground,
          description: clinicalBackground?.name || clinicalBackground?.value
        }
      }
    )

    store.commit('addClinicalBackgroundInfo', {
      clinicalBackground: normalizedResponse,
      propertyName
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/clinicalProfile/addClinicalBackground' },
      { root: true }
    )
  }
}

const removeCpt = async (store, clinicalBackground) => {
  try {
    const { personId, propertyName, value, description } = clinicalBackground
    const normalizedResponse = await instance.delete(
      `persons/${personId}/clinical_profile/cpts/${encodeURIComponent(
        value || description
      )}`
    )

    store.commit('removeClinicalBackgroundInfo', {
      clinicalBackground: normalizedResponse,
      propertyName
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/clinicalProfile/addClinicalBackground' },
      { root: true }
    )
  }
}

export default {
  fetchClinicalProfileByPersonId,
  updateHealthMeasurement,
  getHealthMeasurementTypes,
  addClinicalBackground,
  updateClinicalBackground,
  removeClinicalBackground,
  addCpt,
  removeCpt,
  fetchHealthMeasurements
}
